// @ts-strict
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subscription, tap } from 'rxjs';
import { LMReportFieldPickerService } from './lm-report-field-picker.service';
import { BreakpointChangeService } from '@app/drawer/breakpoint-change.service';
import { ReportFieldPickerConstants } from './lm-report-field-picker.constants';

@Component({
  selector: 'lm-report-field-picker-drawer',
  standalone: false,
  templateUrl: './lm-report-field-picker-drawer.component.html',
  styleUrls: ['./lm-report-field-picker-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LMReportFieldPickerDrawerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() canShowDrawer = false;

  @HostListener('window:resize')
  onResize() {
    // on window resize, recompute scrollbar if visible for proper drawer alignment
    this.cd.markForCheck();
  }

  @HostListener('window:beforeunload', ['$event'])
  onRefresh(event: Event): void {
    if (this.lmReportFieldPickerService?.isCreateReportMode) {
      event.preventDefault();
    }
  }

  readonly DrawerConstants = ReportFieldPickerConstants.Drawer;
  readonly drawerExpandedSize = '300px';
  readonly drawerCollapsedSize = '30px';
  readonly drawerNonReportSize = '0px';
  readonly drawerScrollbarMargin = '14px';

  private subs$: Subscription = new Subscription();
  private isDrawerExpandedSource = new BehaviorSubject<boolean>(false);

  selectedFields$!: Observable<Map<string, string>>;
  isCreateReportMode$!: Observable<boolean>;
  isDrawerExpanded$ = this.isDrawerExpandedSource.asObservable();

  private _isCreateReportDrawerHidden = true;

  get isCreateReportDrawerHidden(): boolean {
    return this._isCreateReportDrawerHidden;
  }

  constructor(
    private lmReportFieldPickerService: LMReportFieldPickerService,
    private breakpointChangeService: BreakpointChangeService,
    private elemRef: ElementRef,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.selectedFields$ = this.lmReportFieldPickerService.selectedFields$;
    this.isCreateReportMode$ = this.lmReportFieldPickerService.isCreateReportMode$.pipe(
      tap((isCreateReportMode) => {
        this._isCreateReportDrawerHidden = !isCreateReportMode;
        this.onExpand(isCreateReportMode);
      })
    );
  }

  ngAfterViewInit(): void {
    this.subs$.add(
      this.breakpointChangeService.isAbovePhoneView$
        .pipe(
          tap((isAbovePhoneView: boolean) => {
            this.onExpand(isAbovePhoneView);
            this.cd.detectChanges();
          })
        )
        .subscribe()
    );

    this.subs$.add(
      this.isDrawerExpanded$
        .pipe(
          tap((isDrawerExpanded) => {
            this.expandDrawer(isDrawerExpanded);
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
  }

  // Event handlers
  onClose() {
    if (this.lmReportFieldPickerService.hasUnsavedChanges) {
      this.subs$.add(this.lmReportFieldPickerService.displayUnsavedReportDialog().subscribe());
    } else {
      this.lmReportFieldPickerService.createReportMode(false);
    }
  }

  onExpand(state: boolean) {
    this.isDrawerExpandedSource.next(state);
  }

  onAddToExistingReport() {
    this.lmReportFieldPickerService.onAddToExistingReport();
  }

  onCreateNewReport() {
    this.lmReportFieldPickerService.onCreateNewReport();
  }

  // Component logic / utilities ----------------
  get hasVisibleScrollBar() {
    const compRef = this.elemRef.nativeElement;
    const parent = compRef.closest('#maincontent');
    return parent?.scrollHeight > parent?.clientHeight;
  }

  private expandDrawer(state: boolean) {
    const compRef = this.elemRef.nativeElement;
    const parent = compRef.closest('#maincontent');

    if (parent) {
      if (this.lmReportFieldPickerService.isCreateReportMode && this.canShowDrawer) {
        parent.style.marginRight = state ? this.drawerExpandedSize : this.drawerCollapsedSize;
      } else {
        parent.style.marginRight = this.drawerNonReportSize;
      }
    }
  }
}
